/*
 * Cart discount codes the theme attaches by itself.
 *
 *  1. Tag pricing codes (snippets/tag-auto-codes.liquid): Bakeyy24 customers
 *     get bakeyy24-blr-5/15/20/25 attached once the DISCOUNTED cart total
 *     reaches ₹2,500, detached below it. (Bakeyy Super codes: same marker,
 *     no threshold, list to be filled in.)
 *  2. "Shipme" (snippets/shipme-auto-discount.liquid) for customers without a
 *     pricing tag once the subtotal reaches ₹1,500.
 *
 * Codes go through cart/update.js — the same call the theme's own discount
 * box makes — and the theme's cart:update event re-renders the cart. Codes
 * attached to the cart carry into checkout, so nothing is typed by hand.
 */
(() => {
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const same = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();
  const sameSet = (a, b) => a.length === b.length && a.every((x) => b.some((y) => same(x, y)));
  const DISMISSED_KEY = 'shipme:dismissed';
  let inFlight = false;
  let timer = null;
  const attempts = {};

  const sectionIds = () => Array.from(document.querySelectorAll('cart-drawer[section-id], main-cart[section-id]'))
    .map((el) => el.getAttribute('section-id')).filter(Boolean);
  const dismissed = () => { try { return sessionStorage.getItem(DISMISSED_KEY) === '1'; } catch (e) { return false; } };

  async function readCodes() {
    const response = await fetch(`${root()}cart.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    const cart = await response.json();
    return (cart.discount_codes || []).map((d) => d.code);
  }

  async function writeCodes(codes) {
    document.dispatchEvent(new CustomEvent('hdt:loading:start'));
    try {
      const response = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: sectionIds() })
      });
      const data = await response.json();
      if (response.ok && data && data.sections) {
        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: { resource: null, sourceId: null, cartData: data, actionAfterATC: null, source: 'cart-discount', data: {} }
        }));
        document.dispatchEvent(new CustomEvent('discount:update', { bubbles: true, detail: { resource: data, sourceId: null } }));
      }
      return data;
    } catch (error) {
      return null;
    } finally {
      document.dispatchEvent(new CustomEvent('hdt:loading:end'));
    }
  }

  // Generic: make sure `wanted` codes are on the cart (or off it), leaving every other code alone.
  // Returns whether every wanted code is confirmed on the cart afterwards.
  async function reconcile(name, wanted, eligible, stateKey) {
    const key = `${eligible ? 'on' : 'off'}:${stateKey}`;
    let codes = await readCodes();
    const onCart = (list) => list.filter((c) => wanted.some((w) => same(w, c)));
    const settled = eligible ? sameSet(onCart(codes), wanted) : onCart(codes).length === 0;
    if (!settled && attempts[name] !== key) {          // one attempt per cart state — no hammering if Shopify rejects a code
      attempts[name] = key;
      const next = codes.filter((c) => !wanted.some((w) => same(w, c)));
      if (eligible) next.push(...wanted);
      const data = await writeCodes(next);
      if (data && data.discount_codes) codes = data.discount_codes.map((d) => d.code);
    }
    return { changed: !settled, applied: sameSet(onCart(codes), wanted) };
  }

  // Tell the checkout gate (assets/bakeyy24.js) whether the pricing codes are really on the cart yet.
  function announceTagCodes(applied) {
    document.querySelectorAll('[data-tag-codes]').forEach((m) => { m.dataset.tagCodesApplied = String(applied); });
    document.dispatchEvent(new CustomEvent('bakeyy:codes-synced', { detail: { applied } }));
  }

  async function syncTagCodes() {
    const marker = document.querySelector('[data-tag-codes]');
    if (!marker) return false;
    const wanted = (marker.dataset.tagCodesList || '').split(',').map((c) => c.trim()).filter(Boolean);
    if (!wanted.length) return false;
    const eligible = marker.dataset.tagCodesEligible === 'true';
    const result = await reconcile('tag', wanted, eligible, marker.dataset.tagCodesTotal);
    announceTagCodes(result.applied);
    return result.changed;
  }

  async function syncShipme() {
    const marker = document.querySelector('[data-shipme-auto]');
    if (!marker) return false;
    const code = marker.dataset.shipmeCode || 'Shipme';
    const eligible = marker.dataset.shipmeEligible === 'true';
    if (eligible && dismissed()) return false;         // customer removed it by hand this session
    return (await reconcile('shipme', [code], eligible, marker.dataset.shipmeSubtotal)).changed;
  }

  async function sync() {
    if (inFlight) return;
    if (!document.querySelector('[data-tag-codes], [data-shipme-auto]')) return;
    inFlight = true;
    try {
      if (!(await syncTagCodes())) await syncShipme();
    } catch (error) {
    } finally {
      inFlight = false;
    }
  }

  document.addEventListener('click', (event) => {
    const pill = event.target && event.target.closest && event.target.closest('.hdt-cart-discount__pill');
    if (!pill || !event.target.closest('.hdt-cart-discount__pill-remove')) return;
    const marker = document.querySelector('[data-shipme-auto]');
    if (same(pill.dataset.discountCode, (marker && marker.dataset.shipmeCode) || 'Shipme')) {
      try { sessionStorage.setItem(DISMISSED_KEY, '1'); } catch (e) {}
    }
  }, true);

  const schedule = (delay) => { window.clearTimeout(timer); timer = window.setTimeout(sync, delay || 200); };
  ['cart:update', 'cart:reload', 'cart:refresh', 'cart:drawer:render', 'cart:main:render'].forEach((name) => document.addEventListener(name, () => schedule(250)));
  const observer = new MutationObserver((mutations) => {
    if (mutations.some((m) => Array.from(m.addedNodes).some((n) => n.nodeType === 1 && (n.matches?.('[data-tag-codes], [data-shipme-auto]') || n.querySelector?.('[data-tag-codes], [data-shipme-auto]'))))) schedule(150);
  });
  const start = () => { observer.observe(document.body, { childList: true, subtree: true }); schedule(300); };
  if (document.body) start(); else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
